import { createContext, useContext } from 'react';

import type { MathKeyboardId } from '../../utils/evaluation/math/mathKeyboardIds';

/**
 * The slice of a mathlive `<math-field>` the custom keyboard drives. Kept minimal (and optional)
 * so this file carries no `mathlive` dependency — `MathLiveField` (shared) can import the hook
 * without pulling the editor, and the panel (in `core`) provides the implementation.
 */
export type MathKeyboardField = HTMLElement & {
  insert?: (latex: string, options?: { selectionMode?: 'placeholder' | 'after' | 'before' | 'all' }) => boolean;
  executeCommand?: (selector: string) => boolean;
  /** `false` when the field has a (non-empty) selection — drives template keys to wrap rather than replace it. */
  readonly selectionIsCollapsed?: boolean;
  focus?: () => void;
  blur?: () => void;
};

export type MathKeyboardContextValue = {
  /** Show the custom keyboard for `field`, rendering `layoutId`'s layout. Called on student-field focus. */
  showKeyboard: (field: MathKeyboardField, layoutId: MathKeyboardId) => void;
  /**
   * Hide the custom keyboard. Pass the blurred `field` from a field's blur handler: the hide is
   * ignored if another field has already become active (focus moved field→field). Call with no
   * argument from ✕ / ⌨ / ↵ to always hide.
   */
  hideKeyboard: (field?: MathKeyboardField) => void;
};

// Default is a no-op so `useMathKeyboard()` is safe outside a provider (e.g. Studio author fields).
export const MathKeyboardContext = createContext<MathKeyboardContextValue>({
  showKeyboard: () => undefined,
  hideKeyboard: () => undefined
});

export const useMathKeyboard = () => useContext(MathKeyboardContext);
